import React, { useState } from 'react';
import { LayoutDashboard, Bell, Users, Rocket } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';

const observabilityFeatures = [
  {
    icon: LayoutDashboard,
    title: 'Hosted Dashboard',
    description: 'Visualize runs, traces, and costs. No infrastructure to manage.',
  },
  {
    icon: Bell,
    title: 'Alerts',
    description: 'Get notified when agents fail or costs spike.',
  },
  {
    icon: Users,
    title: 'Team Collaboration',
    description: 'Shared dashboards, permissions, usage tracking.',
  },
];

const PlatformComingSoon = () => {
  const [email, setEmail] = useState('');
  const { toast } = useToast();

  const handleSubmit = (e) => {
    // Show toast before form submits
    if (email) {
      toast({
        title: "You're on the list!",
        description: "We'll notify you when AgentD Platform launches.",
      });
    }
  };

  return (
    <section className="py-20 px-6 relative overflow-hidden">
      {/* Subtle background distinction */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-500/5 to-transparent"></div>
      
      <div className="container mx-auto max-w-6xl relative z-10">
        {/* Coming Soon Badge */}
        <div className="flex justify-center mb-8">
          <span className="inline-flex items-center px-4 py-2 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-sm font-semibold border border-blue-500/20">
            Coming Soon
          </span>
        </div>

        {/* Headline */}
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight">
            The SDK is just the <span className="text-blue-600 dark:text-blue-400">start</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            AgentD Platform is coming — hosted observability and one-command deployment for AI agents.
          </p>
        </div>

        {/* Deployment Platform Highlight */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 p-8 md:p-12 text-white shadow-2xl border-2 border-blue-400/20">
            <div className="absolute inset-0 bg-grid-white/10"></div>
            <div className="relative z-10 flex flex-col md:flex-row items-center gap-6">
              <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                <Rocket className="h-10 w-10 text-white" />
              </div>
              <div className="flex-1 text-center md:text-left space-y-3">
                <h3 className="text-3xl font-bold">One-Command Deploy</h3>
                <p className="text-blue-100 text-lg">
                  <code className="px-3 py-1 bg-white/10 rounded-md font-mono">agentd deploy</code> — Sandboxed, autoscaling, production-ready agent hosting.
                </p>
                <p className="text-blue-200 text-sm">
                  Deploy your AI agents with zero configuration. We handle infrastructure, scaling, and monitoring.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Observability Features Grid */}
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold">Plus Hosted Observability</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {observabilityFeatures.map((feature, index) => (
            <div 
              key={index} 
              className="flex flex-col items-center text-center space-y-4 p-6 rounded-xl bg-card/50 backdrop-blur-sm border border-border hover:border-blue-500/30 transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <feature.icon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="space-y-2">
                <h4 className="text-lg font-semibold">{feature.title}</h4>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Waitlist Form */}
        <div className="max-w-xl mx-auto">
          <div className="bg-card border border-border rounded-xl p-8 shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-center">Join the Waitlist</h3>
            <form 
              action="https://formsubmit.co/probablyarth@email.com" 
              method="POST"
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              {/* Hidden fields for FormSubmit configuration */}
              <input type="hidden" name="_subject" value="New AgentD Platform Waitlist Signup" />
              <input type="hidden" name="_captcha" value="false" />
              <input type="text" name="_honey" style={{ display: 'none' }} />
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="email"
                  name="email"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="flex-1"
                />
                <Button 
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 whitespace-nowrap"
                >
                  Join the Waitlist
                </Button>
              </div>
              <p className="text-xs text-center text-muted-foreground">
                Be first to know when we launch. No spam.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlatformComingSoon;
